import {
  GET_STYLE_LIST_ENDPOINT,
  POST_SEWING_PROCESS_INFO,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, withTimeout } from "apiServices/common";
import HandleNotification from "common/Notification";
import { createContext, useCallback, useEffect, useState } from "react";
import { isArrayAndHasValue } from "utils/functions";

export const PostSewingProcessContext = createContext();

const PostSewingProcessContextProvider = ({ children }) => {
  // States
  const [styleList, setStyleList] = useState(null);
  const [supplierList, setSupplierList] = useState(null);
  const [selectedStyleDetails, setSelectedStyleDetails] = useState(null);
  const [sewingTableData, setSewingTableData] = useState(null);
  const [showFactoryInputs, setShowFactoryInputs] = useState(false);
  const [subContractStatus, setSubContractStatus] = useState(null);

  const getStyleList = useCallback(async (filterValues) => {
    const query = `${GET_STYLE_LIST_ENDPOINT}`;
    const bodyData = {
      page: filterValues?.page || 1,
      ...filterValues,
    };

    try {
      const response = await withTimeout(getData(query, false, bodyData));

      if (response && response?.data?.code === 200) {
        setStyleList(response?.data?.data?.data);
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Error getting style list",
          null,
        );
      }
    } catch (error) {
      HandleNotification(
        "error",
        "bottomRight",
        error.message || "API Response Timeout",
        null,
      );
    }
  }, []);

  const getSupplierList = useCallback(async (filterValues) => {
    const query = `/api/suppliers`;
    const bodyData = {
      ...filterValues,
    };
    try {
      const response = await getData(query, false, bodyData);
      if (response && response?.data?.code === 200) {
        setSupplierList(response?.data?.data?.data);
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Error getting supplier list",
          null,
        );
      }
    } catch (error) {
      HandleNotification(
        "error",
        "bottomRight",
        error.message || "API Response Timeout",
        null,
      );
    }
  }, []);

  useEffect(() => {
    // Get styles list on first render
    getStyleList();
    getSupplierList();
  }, [getStyleList]);

  const getPostSewingTableData = useCallback(async (props) => {
    const {
      sales_contract_id,
      style_id,
      movement_date,
      buying_office_id,
      sub_contract_factory_id,
    } = props;

    const query = `${POST_SEWING_PROCESS_INFO}`;
    const bodyData = {
      sales_contract_id: sales_contract_id,
      style_id: style_id,
      movement_date: movement_date,
      buying_office_id: buying_office_id || null,
      sub_contract_factory_id: sub_contract_factory_id || null,
    };

    const response = await postData(query, bodyData);
    if (response && response?.code === 200) {
      setSewingTableData(response?.data);
    } else {
      HandleNotification(
        "error",
        "bottomRight",
        "Error getting post sewing process table data",
        null,
      );
    }
  }, []);

  const onSelectStyle = (id) => {
    const styleId = id ? Number(id) : null;
    const selectedStyle = styleList.find((style) => style.id === styleId);
    setSubContractStatus(selectedStyle.subcontract_status);
    if (selectedStyle.buying_office_info) {
      setShowFactoryInputs(false);
    } else if (selectedStyle.factory_info) {
      setShowFactoryInputs(true);
    }
    setSelectedStyleDetails(selectedStyle);
  };

  const onChangeProcessData = (record_id, new_data, context) => {
    const cuttingTableDataCopy = structuredClone(sewingTableData);
    const foundCuttingItem =
      isArrayAndHasValue(cuttingTableDataCopy) &&
      cuttingTableDataCopy?.find((item) => item?.id === record_id);
    foundCuttingItem["process_products"][context] = new_data;

    setSewingTableData(cuttingTableDataCopy);
  };

  return (
    <PostSewingProcessContext.Provider
      value={{
        styleList,
        getPostSewingTableData,
        onSelectStyle,
        selectedStyleDetails,
        getStyleList,
        sewingTableData,
        setSewingTableData,
        showFactoryInputs,
        setShowFactoryInputs,
        subContractStatus,
        setSubContractStatus,
        onChangeProcessData,
        supplierList,
        setSupplierList,
        getSupplierList,
      }}
    >
      {children}
    </PostSewingProcessContext.Provider>
  );
};

export default PostSewingProcessContextProvider;
